// ════════════════════════════════════════════════════════════
// useConfirm + confirmAsync — Helper impératif pour ConfirmDialog
// ════════════════════════════════════════════════════════════
// Permet d'appeler `await confirmAsync({...})` n'importe où dans
// l'app (hors composant) et de récupérer un boolean (true=confirmé,
// false=annulé). Requiert <ConfirmProvider> monté à la racine
// (apps/web/src/routes/__root.tsx) qui rend UN ConfirmDialog
// global lié au store.
// ════════════════════════════════════════════════════════════
import { create } from 'zustand';
import type * as React from 'react';

export interface ConfirmOptions {
  title: string;
  description?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
}

interface ConfirmStoreState {
  open: boolean;
  options: ConfirmOptions | null;
  resolver: ((value: boolean) => void) | null;
  request: (options: ConfirmOptions) => Promise<boolean>;
  resolve: (value: boolean) => void;
  reset: () => void;
}

export const useConfirmStore = create<ConfirmStoreState>((set, get) => ({
  open: false,
  options: null,
  resolver: null,
  request: (options) => {
    // Si un confirm est déjà en cours, on resolve le précédent à false
    const previousResolver = get().resolver;
    if (previousResolver) {
      previousResolver(false);
    }
    return new Promise<boolean>((resolve) => {
      set({ open: true, options, resolver: resolve });
    });
  },
  resolve: (value) => {
    const { resolver } = get();
    if (resolver) {
      resolver(value);
    }
    set({ open: false, resolver: null });
    // Garde `options` pour permettre une animation de sortie propre,
    // sera nettoyé au mount de la prochaine demande ou via reset().
  },
  reset: () => {
    set({ open: false, options: null, resolver: null });
  },
}));

/**
 * Helper impératif — ouvre le ConfirmDialog global et retourne une
 * Promise<boolean>. Requiert <ConfirmProvider> monté.
 *
 * @example
 * if (form.formState.isDirty) {
 *   const ok = await confirmAsync({
 *     title: "Annuler les modifications ?",
 *     description: "Les changements seront perdus.",
 *     confirmLabel: "Annuler",
 *     cancelLabel: "Continuer l'édition",
 *     variant: "destructive",
 *   });
 *   if (!ok) return;
 * }
 */
export function confirmAsync(options: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().request(options);
}
